import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Loader2, MapPin } from 'lucide-react';

/**
 * Address Autocomplete Component using OpenStreetMap Nominatim
 * Provides Canadian address suggestions as user types
 */
const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onAddressSelect, 
  placeholder = "Start typing your address...",
  className = ""
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update input when value prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Fetch address suggestions from Nominatim
  const fetchSuggestions = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // Search for Canadian addresses, prioritizing Manitoba
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `format=json&` +
        `addressdetails=1&` +
        `countrycodes=ca&` +
        `limit=5&` +
        `viewbox=-102.0,49.0,-95.0,60.0&` + // Manitoba bounding box
        `bounded=0`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'ManitobaRCMPCapture/1.0'
          }
        }
      );
      
      const data = await response.json();
      
      // Filter and format results
      const formattedSuggestions = data
        .filter(item => item.address)
        .map(item => ({
          id: item.place_id,
          displayName: item.display_name,
          streetNumber: item.address.house_number || '',
          street: item.address.road || item.address.street || '',
          city: item.address.city || item.address.town || item.address.village || item.address.municipality || '',
          province: item.address.state || 'Manitoba',
          postalCode: item.address.postcode || '',
          country: item.address.country || 'Canada',
          lat: item.lat,
          lon: item.lon
        }));
      
      setSuggestions(formattedSuggestions);
      setShowSuggestions(formattedSuggestions.length > 0);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange && onChange(newValue);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    const streetAddress = suggestion.streetNumber 
      ? `${suggestion.streetNumber} ${suggestion.street}` 
      : suggestion.street;
    
    setInputValue(streetAddress);
    setShowSuggestions(false);
    setSuggestions([]);

    // Call parent callback with parsed address
    if (onAddressSelect) {
      onAddressSelect({
        streetAddress: streetAddress,
        city: suggestion.city,
        province: suggestion.province,
        postalCode: suggestion.postalCode,
        latitude: parseFloat(suggestion.lat),
        longitude: parseFloat(suggestion.lon)
      });
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pr-10"
          autoComplete="off"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              className="flex items-start px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              <MapPin className="w-4 h-4 text-blue-600 mt-1 mr-2 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {suggestion.streetNumber} {suggestion.street}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {suggestion.city}, {suggestion.province} {suggestion.postalCode}
                </p>
              </div>
            </div>
          ))}
          <div className="px-3 py-1 text-xs text-gray-400 border-t bg-gray-50">
            Powered by OpenStreetMap
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
